import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Calendar, Clock, MessageSquare, Trash2, PlusCircle,
    Search, User, Loader2, AlertCircle, CalendarDays, X, Check
} from 'lucide-react';

// --- SERVIÇOS LOCAIS ---
import api from '../services/api';
import { socket } from '../services/socket';
import Modal from '../components/Modal';

const AgendamentosScreen = ({ currentUser, clients = [] }) => {
    // --- ESTADOS ---
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);

    // Estado do Formulário
    const [formData, setFormData] = useState({
        clientId: '',
        clientName: '', // Para exibição no input
        date: '',
        time: '',
        message: ''
    });

    // --- ESTADOS DO AUTOCOMPLETE DE CLIENTES (NOVO) ---
    const [clientSearchTerm, setClientSearchTerm] = useState('');
    const [showClientSuggestions, setShowClientSuggestions] = useState(false);
    const wrapperRef = useRef(null); // Para detectar clique fora

    // Estado do Modal de Exclusão
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // Filtro da Lista de Agendamentos (Lado Direito)
    const [listSearchTerm, setListSearchTerm] = useState('');

    // ==================================================================================
    // 1. CARREGAMENTO E SOCKET
    // ==================================================================================

    useEffect(() => {
        fetchSchedules();

        socket.on('schedules_updated', () => {
            fetchSchedules();
        });

        // Fecha a lista de sugestões ao clicar fora
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowClientSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            socket.off('schedules_updated');
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const fetchSchedules = async () => {
        try {
            const res = await api.get('/scheduled-messages');
            setSchedules(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error("Erro ao buscar agendamentos:", error);
            setSchedules([]);
        } finally {
            setLoading(false);
        }
    };

    // ==================================================================================
    // 2. LÓGICA DE AUTOCOMPLETE (CLIENTES)
    // ==================================================================================

    // Filtra a lista de clientes para o Dropdown Inteligente
    const filteredClientSuggestions = useMemo(() => {
        if (!clientSearchTerm) return [];
        const term = clientSearchTerm.toLowerCase();
        return clients.filter(c =>
            (c.name && c.name.toLowerCase().includes(term)) ||
            (c.phone && c.phone.includes(term))
        ).slice(0, 10); // Limita a 10 resultados para performance visual
    }, [clients, clientSearchTerm]);

    const handleSelectClient = (client) => {
        setFormData(prev => ({
            ...prev,
            clientId: client.id || client._id,
            clientName: client.name || client.phone
        }));
        setClientSearchTerm(''); // Limpa a busca visual
        setShowClientSuggestions(false); // Fecha a lista
    };

    const handleClearClientSelection = () => {
        setFormData(prev => ({ ...prev, clientId: '', clientName: '' }));
        setClientSearchTerm('');
    };

    // ==================================================================================
    // 3. AÇÕES (CRIAR E DELETAR)
    // ==================================================================================

    const handleScheduleMessage = async (e) => {
        e.preventDefault();

        if (!formData.clientId || !formData.date || !formData.time || !formData.message) {
            alert("Por favor, preencha todos os campos e selecione um cliente da lista.");
            return;
        }

        const scheduledDate = new Date(`${formData.date}T${formData.time}`);
        const now = new Date();
        if (scheduledDate < now) {
            alert("A data/hora do agendamento deve ser futura.");
            return;
        }

        setProcessing(true);

        try {
            const selectedClient = clients.find(c => (c.id || c._id) === formData.clientId);

            const payload = {
                clientId: formData.clientId,
                clientName: selectedClient?.name || formData.clientName,
                clientPhone: selectedClient?.phone,
                scheduledAt: scheduledDate.toISOString(),
                message: formData.message,
                authorId: currentUser.id,
                authorName: currentUser.name,
                status: 'pending'
            };

            await api.post('/scheduled-messages', payload);

            // Resetar formulário
            setFormData({ clientId: '', clientName: '', date: '', time: '', message: '' });
            setClientSearchTerm('');

            alert("Mensagem agendada com sucesso!");
            fetchSchedules();

        } catch (error) {
            console.error(error);
            alert("Erro ao agendar mensagem.");
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;
        setProcessing(true);
        try {
            const idToDelete = itemToDelete.id || itemToDelete._id;
            await api.delete(`/scheduled-messages/${idToDelete}`);
            fetchSchedules();
            setDeleteModalOpen(false);
            setItemToDelete(null);
        } catch (error) {
            alert("Erro ao excluir agendamento.");
        } finally {
            setProcessing(false);
        }
    };

    // ==================================================================================
    // 4. RENDERIZAÇÃO
    // ==================================================================================

    // Filtra a lista de agendamentos já criados (coluna direita)
    const filteredSchedules = useMemo(() => {
        if (!Array.isArray(schedules)) return [];
        return schedules.filter(item =>
            (item.clientName || '').toLowerCase().includes(listSearchTerm.toLowerCase()) ||
            (item.message || '').toLowerCase().includes(listSearchTerm.toLowerCase())
        );
    }, [schedules, listSearchTerm]);

    return (
        <div className="flex flex-col md:flex-row h-full bg-gray-50 overflow-hidden font-sans">

            {/* --- COLUNA ESQUERDA: FORMULÁRIO --- */}
            <div className="w-full md:w-1/3 bg-white p-6 border-r border-gray-200 overflow-y-auto">
                <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                    <CalendarDays className="mr-2 text-blue-600" /> Novo Agendamento
                </h2>

                <form onSubmit={handleScheduleMessage} className="space-y-4">

                    {/* 🚨 CAMPO DE CLIENTE COM AUTOCOMPLETE 🚨 */}
                    <div ref={wrapperRef} className="relative">
                        <label className="block text-sm font-bold text-gray-700 mb-1">Cliente</label>

                        {formData.clientId ? (
                            // Visualização quando um cliente já está selecionado
                            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 text-blue-800 p-2.5 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <User className="w-4 h-4" />
                                    <span className="font-bold text-sm truncate max-w-[200px]">{formData.clientName}</span>
                                </div>
                                <button type="button" onClick={handleClearClientSelection} className="text-blue-400 hover:text-blue-600 p-1">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            // Campo de Busca
                            <div className="relative">
                                <input
                                    type="text"
                                    className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                    placeholder="Digite o nome ou telefone..."
                                    value={clientSearchTerm}
                                    onChange={(e) => {
                                        setClientSearchTerm(e.target.value);
                                        setShowClientSuggestions(true);
                                    }}
                                    onFocus={() => setShowClientSuggestions(true)}
                                />
                                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />

                                {/* LISTA FLUTUANTE DE SUGESTÕES */}
                                {showClientSuggestions && clientSearchTerm && (
                                    <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-xl mt-1 max-h-60 overflow-y-auto">
                                        {filteredClientSuggestions.length > 0 ? (
                                            filteredClientSuggestions.map(client => (
                                                <div
                                                    key={client.id || client._id}
                                                    onClick={() => handleSelectClient(client)}
                                                    className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-none flex flex-col"
                                                >
                                                    <span className="font-bold text-gray-800 text-sm">{client.name}</span>
                                                    <span className="text-xs text-gray-500">{client.phone}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-3 text-sm text-gray-400 text-center">Nenhum cliente encontrado.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Data</label>
                            <input
                                type="date"
                                className="w-full p-2.5 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.date}
                                onChange={e => setFormData({ ...formData, date: e.target.value })}
                                min={new Date().toISOString().split('T')[0]}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Hora</label>
                            <input
                                type="time"
                                className="w-full p-2.5 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.time}
                                onChange={e => setFormData({ ...formData, time: e.target.value })}
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Mensagem</label>
                        <textarea
                            rows="6"
                            className="w-full p-3 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                            placeholder="Olá, gostaríamos de confirmar..."
                            value={formData.message}
                            onChange={e => setFormData({ ...formData, message: e.target.value })}
                            required
                        />
                        <p className="text-xs text-right text-gray-400 mt-1">
                            {formData.message.length} caracteres
                        </p>
                    </div>

                    <button
                        type="submit"
                        disabled={processing || !formData.clientId}
                        className={`w-full py-3 rounded-lg font-bold shadow-md flex items-center justify-center transition-transform active:scale-95 
                            ${!formData.clientId ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                        {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : <PlusCircle className="w-5 h-5 mr-2" />}
                        Agendar Envio
                    </button>
                </form>

                <div className="mt-8 bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <h4 className="text-sm font-bold text-blue-800 mb-2 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-2" /> Como funciona?
                    </h4>
                    <p className="text-xs text-blue-700 leading-relaxed">
                        A mensagem será enviada automaticamente pelo servidor na data e hora escolhidas.
                        Certifique-se de que o WhatsApp esteja conectado no momento do envio.
                    </p>
                </div>
            </div>

            {/* --- COLUNA DIREITA: LISTA DE AGENDAMENTOS --- */}
            <div className="flex-1 p-6 overflow-y-auto">
                <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Fila de Envios</h1>
                        <p className="text-sm text-gray-500">Gerencie as mensagens programadas.</p>
                    </div>

                    <div className="relative w-full md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            className="w-full pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                            placeholder="Buscar na fila..."
                            value={listSearchTerm}
                            onChange={e => setListSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 text-blue-500 animate-spin" /></div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {filteredSchedules.map(item => {
                            const canDelete = currentUser.role === 'admin' || currentUser.role === 'supervisor' || item.authorId === currentUser.id;
                            const scheduledDate = new Date(item.scheduledAt);

                            return (
                                <div key={item.id} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all group relative">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center">
                                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold mr-3">
                                                {item.clientName?.charAt(0).toUpperCase() || '?'}
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-gray-800 text-sm">{item.clientName || 'Cliente'}</h3>
                                                <p className="text-xs text-gray-400">{item.clientPhone}</p>
                                            </div>
                                        </div>
                                        {canDelete && (
                                            <button
                                                onClick={() => handleDeleteClick(item)}
                                                className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                                title="Excluir Agendamento"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>

                                    <div className="bg-gray-50 p-3 rounded-lg mb-3 text-sm text-gray-600 line-clamp-3 italic relative">
                                        <MessageSquare className="w-4 h-4 text-gray-300 absolute top-2 right-2" />
                                        "{item.message}"
                                    </div>

                                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                        <div className="flex items-center text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                            <Calendar className="w-3 h-3 mr-1" />
                                            {scheduledDate.toLocaleDateString()}
                                            <span className="mx-1">•</span>
                                            <Clock className="w-3 h-3 mr-1" />
                                            {scheduledDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>

                                        <div className="text-xs text-gray-400 flex items-center" title={`Criado por ${item.authorName}`}>
                                            <User className="w-3 h-3 mr-1" /> {item.authorName?.split(' ')[0]}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {filteredSchedules.length === 0 && (
                            <div className="col-span-full text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
                                <CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 font-medium">Nenhum agendamento encontrado.</p>
                                <p className="text-sm text-gray-400">Use o formulário ao lado para criar o primeiro.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* MODAL DE EXCLUSÃO */}
            <Modal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Confirmar Exclusão"
                confirmText="Sim, excluir"
                onConfirm={handleDeleteConfirm}
                isLoading={processing}
            >
                <div className="text-center py-2">
                    <Trash2 className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-gray-700 mb-1">Tem certeza que deseja cancelar este envio?</p>
                    <p className="text-sm text-gray-500">
                        Cliente: <strong>{itemToDelete?.clientName}</strong><br />
                        Data: {itemToDelete && new Date(itemToDelete.scheduledAt).toLocaleString()}
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default AgendamentosScreen;